import React, { useRef, useState } from "react";

import { useElementSize, useTranslation } from "@opendash/core";

import { Button, Col, Drawer, Input, Row, Tabs } from "antd";
import * as L from "leaflet";

import { Icon } from "@opendash/icons";
import { CustomParseFieldProps } from "@opendash/plugin-parse";
import * as turf from "@turf/turf";

export const EditGeoJSON = (
  props: CustomParseFieldProps<any> & {
    nameField?: string;
    descriptionField?: string;
  }
) => {
  const t = useTranslation();
  const { value, setValue, fieldname, parseObject } = props;
  const map = React.useRef(null);

  const container = React.useRef(null);
  const drawerContent = React.useRef(null);
  const [open, setOpen] = useState(false);
  const { width, height } = useElementSize(container);
  const editLayer = useRef(null);
  const [geoJSON, setGeoJSON] = useState(value);
  const [manualString, setManualString] = useState<string>(
    JSON.stringify(value, null, 2)
  );
  const [tab, setTab] = useState("1");
  const { width: widthContainer, height: heightContainer } =
    useElementSize(drawerContent);
  const editLayerEvent = (e) => {
    if (editLayer.current !== null) {
      editLayer.current.pm.disable();
    }
    editLayer.current = e.layer;
    e.layer.pm.enable({
      limitMarkersToCount: 20,
    });
  };
  React.useEffect(() => {
    if (container.current && open) {
      if (map.current) {
        map.current.eachLayer(function (layer) {
          map.current.removeLayer(layer);
        });
      }
      map.current = L.map(container.current, {
        center: [0, 0],
        zoom: 1,
        //@ts-ignore
        layers: [L.tileLayer(...window.leafletTiles)],
        //@ts-ignore
        gestureHandling: false,

        pmIgnore: false,
      });

      map.current.dragging.enable();
      map.current.scrollWheelZoom.enable();
      const cLayer = L.geoJSON(value);
      map.current.on("pm:create", (e) => {
        e.layer.on("click", editLayerEvent(e));
        e.layer.on("pm:update", console.log("update", e));
      });
      map.current.on("pm:udpate", (e) => {});
      map.current.on("pm:delete", (e) => {});

      cLayer.on("click", editLayerEvent);

      cLayer.addTo(map.current);
      map.current.pm.addControls({
        drawControls: true,
        editControls: true,
        optionsControls: true,
        customControls: true,
        editMode: false,
        oneBlock: false,
        rotateMode: false,
        drawText: false,
        drawCircle: false,
        drawCircleMarker: false,
      });

      setTimeout(() => {
        map.current.fitBounds(cLayer.getBounds());
        map.current.invalidateSize();
      }, 500);
      setGeoJSON(cLayer);
      return () => {
        map.current.remove();
      };
    }
  }, [container.current, open]);

  React.useEffect(() => {
    if (map.current && open) {
      try {
        map.current.invalidateSize();
      } catch (error) {
        // console.error(error);
      }
    }
  }, [map.current, open, width, height]);
  return (
    <>
      <Button
        type="default"
        onClick={() => {
          setOpen(true);
        }}
      >
        <Icon icon="fa:edit" />
      </Button>

      <Drawer
        open={open}
        title={value.label}
        height={"80%"}
        extra={[
          <>
            <Button
              type="link"
              icon={<Icon icon="fa:undo" />}
              onClick={() => {
                setManualString(JSON.stringify(value, null, 2));
                setGeoJSON(value);
              }}
            />
          </>,
          <Button
            type="link"
            icon={<Icon icon="fa:save" />}
            disabled={tab === "2" && !isJson(manualString)}
            onClick={() => {
              if (tab === "1") {
                map.current.pm.disableGlobalEditMode();
                const features = [];
                map.current.eachLayer((layer) => {
                  if (layer.toGeoJSON) {
                    const cLayer = layer.toGeoJSON();
                    if (cLayer.type === "FeatureCollection") {
                      return;
                    }
                    if (!cLayer.properties?.name) {
                      if (!cLayer.properties) {
                        cLayer.properties = {};
                      }
                      cLayer.properties.name =
                        props.parseObject.get(props.nameField || "label") ||
                        "Feature_" + features.length + 1;
                      cLayer.properties.description = props.parseObject.get(
                        props.descriptionField || "description"
                      );
                    }
                    features.push(cLayer);
                  }
                });
                const featureCollection = turf.featureCollection(features);
                parseObject.set("geo", featureCollection);
                setManualString(JSON.stringify(featureCollection, null, 2));
              }

              if (tab === "2") {
                const obj = JSON.parse(manualString);
                let toSave = obj;
                if (obj.type !== "FeatureCollection") {
                  let feature = obj;
                  if (obj.type !== "Feature") {
                    feature = turf.feature(obj);
                  }
                  toSave = turf.featureCollection([feature]);
                }
                parseObject.set("geo", toSave);
                setManualString(JSON.stringify(toSave, null, 2));
              }
              parseObject.save().then((res) => {
                if (geoJSON) {
                  map.current.removeLayer(geoJSON);
                  const newLayer = L.geoJSON(res.get("geo"));
                  newLayer.addTo(map.current);
                  setTimeout(() => {
                    map.current.fitBounds(newLayer.getBounds());
                    map.current.invalidateSize();
                  }, 500);
                }
              });
              //setOpen(false);
            }}
          />,
        ]}
        placement="bottom"
        onClose={() => setOpen(false)}
      >
        <Row ref={drawerContent} style={{ height: "100%" }}>
          <Col span={24} style={{ height: "100%" }}>
            <Tabs
              activeKey={tab}
              style={{ width: "100%" }}
              centered
              onChange={(key) => {
                if (key === "1") {
                  setTimeout(() => {
                    map.current.invalidateSize();
                  }, 500);
                }
                setTab(key);
              }}
            >
              <Tabs.TabPane
                tab={<Icon icon="fa:pen" />}
                style={{ width: "100%", height: "100%" }}
                key="1"
              >
                <div
                  ref={container}
                  style={{ height: heightContainer - 50, width: "100%" }}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab={<Icon icon="fa:code" />} key="2">
                <Input.TextArea
                  style={{ width: "100%", height: heightContainer - 50 }}
                  value={manualString}
                  onChange={(e) => {
                    const val = e.target.value;
                    setManualString(val);
                  }}
                />
              </Tabs.TabPane>
            </Tabs>
          </Col>
        </Row>
      </Drawer>
    </>
  );
};
export default EditGeoJSON;

function isJson(str) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}
